/**
 * AcrConfigForm Component
 *
 * Form for configuring Azure Container Registry settings.
 *
 * Feature: 017-backup-infrastructure-setup
 * Task: T016 - Create AcrConfigForm component
 * Constitution: v1.8.0 (Multi-Subscription SQL MI Edition)
 *
 * Requirements:
 * - FR-001: Configure ACR settings
 * - FR-002: Validate ACR connectivity
 */

import React, { useState, useEffect } from 'react';
import {
  AcrConfigDto,
  AciConfigDto,
  KeyVaultConfigDto,
  InfrastructureConfigUpdateDto,
  ConfigValidationResult,
} from '../types';
import { useValidateConfig, useUpdateInfrastructureConfig } from '../hooks/useInfrastructure';

interface AcrConfigFormProps {
  initialData?: AcrConfigDto;
  aciConfig: AciConfigDto;
  keyVaultConfig: KeyVaultConfigDto;
  onSuccess?: () => void;
  onValidationResult?: (result: ConfigValidationResult | null) => void;
}

interface FormErrors {
  registryName?: string;
  resourceGroup?: string;
  subscriptionId?: string;
  submit?: string;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ACR_NAME_REGEX = /^[a-zA-Z0-9]{5,50}$/;
const RESOURCE_GROUP_REGEX = /^[a-zA-Z0-9_\-().]{1,90}$/;

export const AcrConfigForm: React.FC<AcrConfigFormProps> = ({
  initialData,
  aciConfig,
  keyVaultConfig,
  onSuccess,
  onValidationResult,
}) => {
  const [formData, setFormData] = useState({
    registryName: initialData?.registryName || '',
    resourceGroup: initialData?.resourceGroup || '',
    subscriptionId: initialData?.subscriptionId || '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [validationResult, setValidationResult] = useState<ConfigValidationResult | null>(null);

  const validateMutation = useValidateConfig();
  const updateMutation = useUpdateInfrastructureConfig();

  // Update form when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        registryName: initialData.registryName || '',
        resourceGroup: initialData.resourceGroup || '',
        subscriptionId: initialData.subscriptionId || '',
      });
    }
  }, [initialData]);

  // Notify parent of validation result changes
  useEffect(() => {
    onValidationResult?.(validationResult);
  }, [validationResult, onValidationResult]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Only validate if at least one field is filled
    const hasAnyValue = formData.registryName || formData.resourceGroup || formData.subscriptionId;

    if (hasAnyValue) {
      // Registry name validation
      if (!formData.registryName.trim()) {
        newErrors.registryName = 'Registry name is required when configuring ACR';
      } else if (!ACR_NAME_REGEX.test(formData.registryName)) {
        newErrors.registryName = 'Registry name must be 5-50 alphanumeric characters';
      }

      // Resource group validation
      if (!formData.resourceGroup.trim()) {
        newErrors.resourceGroup = 'Resource group is required when configuring ACR';
      } else if (!RESOURCE_GROUP_REGEX.test(formData.resourceGroup)) {
        newErrors.resourceGroup = 'Invalid resource group name format';
      }

      // Subscription ID validation
      if (!formData.subscriptionId.trim()) {
        newErrors.subscriptionId = 'Subscription ID is required when configuring ACR';
      } else if (!UUID_REGEX.test(formData.subscriptionId)) {
        newErrors.subscriptionId = 'Subscription ID must be a valid UUID';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof typeof formData, value: string): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for the field being edited
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    // Clear validation result when user makes changes
    setValidationResult(null);
  };

  const buildConfigPayload = (): InfrastructureConfigUpdateDto => ({
    acr: formData.registryName
      ? {
          registryName: formData.registryName,
          resourceGroup: formData.resourceGroup,
          subscriptionId: formData.subscriptionId,
        }
      : undefined,
    aci: aciConfig,
    keyVault: keyVaultConfig,
  });

  const handleValidate = async (): Promise<void> => {
    if (!validateForm()) return;

    setValidationResult(null);
    setErrors({});

    try {
      const result = await validateMutation.mutateAsync(buildConfigPayload());
      setValidationResult(result);
    } catch (err) {
      setErrors({
        submit: err instanceof Error ? err.message : 'Validation failed',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!validateForm()) return;

    setErrors({});

    try {
      await updateMutation.mutateAsync(buildConfigPayload());
      onSuccess?.();
    } catch (err: any) {
      const details: { field: string; message: string }[] = err?.response?.data?.details ?? [];
      if (details.length > 0) {
        setErrors({ submit: details.map(d => `${d.field}: ${d.message}`).join(' | ') });
      } else {
        setErrors({ submit: err instanceof Error ? err.message : 'Failed to save configuration' });
      }
    }
  };

  const getValidationStatusColor = (status?: string): string => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 dark:text-green-400';
      case 'degraded':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'unavailable':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-500 dark:text-gray-400';
    }
  };

  const getValidationStatusIcon = (status?: string): string => {
    switch (status) {
      case 'healthy':
        return '✓';
      case 'degraded':
        return '⚠';
      case 'unavailable':
        return '✗';
      default:
        return '?';
    }
  };

  return (
    <div className='bg-[var(--color-bg-card)] rounded-lg shadow-sm border border-[var(--color-border)]'>
      {/* Header */}
      <div className='px-6 py-4 border-b border-[var(--color-border)]'>
        <div className='flex items-center justify-between'>
          <div>
            <h3 className='text-lg font-medium text-[var(--color-text-primary)]'>Azure Container Registry</h3>
            <p className='mt-1 text-sm text-[var(--color-text-secondary)]'>
              Configure the container registry for backup utility images
            </p>
          </div>
          {validationResult?.checks?.acr && (
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                validationResult.checks.acr.status === 'healthy'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                  : validationResult.checks.acr.status === 'unavailable'
                    ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              {getValidationStatusIcon(validationResult.checks.acr.status)}{' '}
              {validationResult.checks.acr.status === 'healthy'
                ? 'Connected'
                : validationResult.checks.acr.status === 'unavailable'
                  ? 'Unreachable'
                  : 'Unknown'}
            </span>
          )}
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className='p-6 space-y-4'>
        {/* Submit Error */}
        {errors.submit && (
          <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-md text-sm'>
            {errors.submit}
          </div>
        )}

        {/* Validation Result */}
        {validationResult?.checks?.acr && (
          <div
            className={`px-4 py-3 rounded-md text-sm ${
              validationResult.checks.acr.status === 'healthy'
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                : validationResult.checks.acr.status === 'unavailable'
                  ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
                  : 'bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            <span
              className={`font-medium ${getValidationStatusColor(validationResult.checks.acr.status)}`}
            >
              {getValidationStatusIcon(validationResult.checks.acr.status)}
            </span>{' '}
            {validationResult.checks.acr.message}
          </div>
        )}

        {/* Registry Name */}
        <div>
          <label htmlFor='registryName' className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'>
            Registry Name
          </label>
          <input
            type='text'
            id='registryName'
            value={formData.registryName}
            onChange={(e) => handleInputChange('registryName', e.target.value)}
            placeholder='acrfabricbackup'
            className={`w-full px-3 py-2 border rounded-md shadow-sm bg-[var(--color-bg-card)] text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.registryName ? 'border-red-300 dark:border-red-600' : 'border-[var(--color-border)]'
            }`}
          />
          {errors.registryName ? (
            <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.registryName}</p>
          ) : (
            <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>
              5-50 alphanumeric characters (no hyphens or underscores)
            </p>
          )}
        </div>

        {/* Resource Group */}
        <div>
          <label htmlFor='resourceGroup' className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'>
            Resource Group
          </label>
          <input
            type='text'
            id='resourceGroup'
            value={formData.resourceGroup}
            onChange={(e) => handleInputChange('resourceGroup', e.target.value)}
            placeholder='rg-fabric-backup'
            className={`w-full px-3 py-2 border rounded-md shadow-sm bg-[var(--color-bg-card)] text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.resourceGroup ? 'border-red-300 dark:border-red-600' : 'border-[var(--color-border)]'
            }`}
          />
          {errors.resourceGroup && (
            <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.resourceGroup}</p>
          )}
        </div>

        {/* Subscription ID */}
        <div>
          <label htmlFor='subscriptionId' className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'>
            Subscription ID
          </label>
          <input
            type='text'
            id='subscriptionId'
            value={formData.subscriptionId}
            onChange={(e) => handleInputChange('subscriptionId', e.target.value)}
            placeholder='00000000-0000-0000-0000-000000000000'
            className={`w-full px-3 py-2 border rounded-md shadow-sm bg-[var(--color-bg-card)] text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.subscriptionId ? 'border-red-300 dark:border-red-600' : 'border-[var(--color-border)]'
            }`}
          />
          {errors.subscriptionId && (
            <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.subscriptionId}</p>
          )}
        </div>

        {/* Computed Login Server (Read-only) */}
        {formData.registryName && (
          <div>
            <label className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'>Login Server</label>
            <div className='px-3 py-2 bg-[var(--color-bg-subtle)] border border-[var(--color-border)] rounded-md text-sm text-[var(--color-text-secondary)]'>
              {formData.registryName}.azurecr.io
            </div>
          </div>
        )}

        {/* Actions */}
        <div className='flex items-center justify-end space-x-3 pt-4'>
          <button
            type='button'
            onClick={handleValidate}
            disabled={validateMutation.isPending || !formData.registryName}
            className='px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-md shadow-sm hover:bg-[var(--color-bg-subtle)] focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {validateMutation.isPending ? (
              <span className='flex items-center'>
                <svg className='animate-spin -ml-1 mr-2 h-4 w-4' fill='none' viewBox='0 0 24 24'>
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  />
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                  />
                </svg>
                Validating...
              </span>
            ) : (
              'Test Connection'
            )}
          </button>
          <button
            type='submit'
            disabled={updateMutation.isPending}
            className='px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {updateMutation.isPending ? (
              <span className='flex items-center'>
                <svg className='animate-spin -ml-1 mr-2 h-4 w-4' fill='none' viewBox='0 0 24 24'>
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  />
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                  />
                </svg>
                Saving...
              </span>
            ) : (
              'Save Configuration'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AcrConfigForm;
